/* eslint-disable react/jsx-pascal-case */
/* eslint-disable max-nested-callbacks */
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * AppRoutes
 * @author Chavan Dinesh <chavan.dinesh@ahex.co.in>
 */

import React, { useRef, useState, Suspense, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { API } from '../api';
import Redirect from './Redirect';
import jwt_decode from 'jwt-decode';
import { useLogout } from '../hooks';
import { toast } from 'react-toastify';
import { store } from '../redux/store';
import RoleRedirect from './RoleRedirect';
import SecuredRoutes from './SecuredRoutes';
import Error from '../components/ErrorPage';
import AppLoader from '../components/AppLoader';
import { useAuth0 } from '@auth0/auth0-react';
import { AGILE_ORG } from '../utils/constants';
import ProtectedRoute from './ProtectedRoutes';
import ProfileOrgSettingsGuard from './ProfileOrgSettingsGuard';
import CapabilitySetupGuard from './CapabilitySetupGuard';
import HiringGuard from './HiringGuard';
import { isCandidateRefusal, rememberCandidateRefusal } from '../api/hiring';
import useCapabilityRelease, { useCapabilityReleaseForMembers } from '../hooks/useCapabilityRelease';
import { setCapabilityRelease } from '../utils/capabilityRelease';
import { useModule } from '../utils/useModule';
import Exception from '../components/Exception';
import TemplePage from '../constants/TemplePage';
import NotFoundv2 from '../components/NotFoundv2';
import { getUserDetails } from '../utils/orgName';
import { getProgramContext } from '../hooks/useProgramContext';
import ROLES, { rolesArray } from '../utils/roles';
import { useUserRole } from '../utils/useUserRole';
import MobileAppLinkPage from './MobileAppLinkPage';
import { checkSession } from '../utils/checkSeesion';
import useCallDetails from '../queries/useCallDetails';
import { ChatClient } from '@azure/communication-chat';
import Layout from '../pages/new-designs/layout/Layout';
import AIMDv2Page from '../pages/new-designs/tabs/aimd-v2/AIMDv2Page';
import HomeTab from '../pages/new-designs/tabs/HomeTab';
// import useGroupSettings from '../hooks/useGroupSettigs';
import ChatTab from '../pages/new-designs/tabs/chat/Chat';
import Charlie from '../pages/new-designs/tabs/charlie/Charlie';
import Mentee from '../pages/new-designs/tabs/home/Mentee';
import Mentor from '../pages/new-designs/tabs/home/Mentor';
import useProfileDetails from '../hooks/useProfileDetails';
import Knowledge from '../pages/new-designs/tabs/Knowledge';
import CapabilitySetupPage from '../pages/new-designs/tabs/capability-setup/CapabilitySetupPage';
import HiringPage from '../pages/new-designs/tabs/hiring/HiringPage';
import HiringRoundPage from '../pages/new-designs/tabs/hiring/RoundPage';
import CandidateApp from '../pages/hiring/CandidateApp';
import OperationsPage from '../pages/new-designs/tabs/capability-setup/operations/OperationsPage';
import AssessmentPlayerPage from '../pages/new-designs/tabs/capability-setup/learner/AssessmentPlayerPage';
import LogoutComponent from '../components/LogoutComponent';
import HelpTab from '../pages/new-designs/tabs/Help/HelpTab';
import { companyActions } from '../redux/slices/companySlice';
import CommunityTab from '../pages/new-designs/tabs/Community';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import Profile from '../pages/new-designs/tabs/profile/Profile';
import ConnectionProblem from '../components/ConnectionProblem';
import ChatBot from '../pages/new-designs/tabs/livechat/ChatBot';
// The whole app leans on antd's global reset (html/body height: 100%, border-box
// on everything), which used to arrive here through the static ZoomIndex import.
// With ZoomIndex lazy, every height: 100% chain collapsed (blank Program Design,
// pages stopping short of the viewport). Keep it in the first load, in the same
// cascade position, until the app owns that reset itself.
import 'antd/dist/antd.min.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCallsCount } from '../redux/slices/callCountSlice';
import PlannedMaintenance from '../components/PlannedMaintenance';
import { fetchChatUsers } from '../redux/slices/getChatUsersSlice';
import AskDosen from '../pages/new-designs/tabs/livechat/AskDosen';
import LiveChat from '../pages/new-designs/tabs/livechat/LiveChat';
import {
  modifyCommunity,
  fetchCommunityMembers,
  communityUsersActions
} from '../redux/slices/acs-community-members/acsCommunityMembers';
import MenteeTask from '../pages/new-designs/tabs/home/MenteeTask';
import ElliottLessonGate from '../pages/new-designs/tabs/home/elliott-lesson/ElliottLessonGate';
import Calendar from '../pages/new-designs/tabs/calendar/Calendar';
import ThemeReference from '../components/ui/theme/ThemeReference';
import ComingSoon from '../pages/new-designs/components/ComingSoon';
import { fetchMentorTasks } from '../redux/slices/mentorTasksSlice';
import ProfileLayout from '../pages/new-designs/tabs/ProfileLayout';
import {
  CourseCreationTimeElement,
  ElliottConversationsElement,
  LoginElement,
  OrganizationsElement,
  ZoomIndexElement
} from './lazyElements';
import { popupCountActions } from '../redux/slices/poupsCountsSlice';
import LibraryTab from '../pages/new-designs/tabs/Library/LibraryTab';
import AiChatMain from '../pages/new-designs/tabs/livechat/AiChatMain';
import PromptChat from '../pages/new-designs/tabs/livechat/PromptChat';
import PromptCards from '../pages/new-designs/tabs/livechat/PrompCards';
import CommunityMember from '../pages/new-designs/tabs/CommunityMember';
import DeactivatedUser from '../pages/new-designs/tabs/DeactivatedUser';
import { fetchUserGroupList } from '../redux/slices/groupRolesListSlice';
import { NewChatBot } from '../pages/new-designs/tabs/livechat/NewChatBot';
import LibraryIndustries from '../pages/new-designs/tabs/LibraryIndustries';
import { fetchOrgDetails } from '../redux/slices/orgDetails/orgDetailsSlice';
import { acsChannelActions } from '../redux/slices/acs-channels/acsChannels';
import AIChatThanksPage from '../pages/new-designs/tabs/livechat/ThanksPage';
import { selectedChatUserActions } from '../redux/slices/selectedChatUserSlice';
import ProfileSettings from '../pages/new-designs/tabs/profile/ProfileSettings';
import MatchesMainPage from '../pages/new-designs/tabs/Matches/MatchesMainPage';
import ProgramMatches from '../pages/new-designs/tabs/Matches/ProgramMatches';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import { fetchUserProfile } from '../redux/slices/user-profile/userProfieSlice';
import { fetchAllArticlesUser } from '../redux/slices/article/getAllArticleUserSlice';
import { fetchUserWorkHistory } from '../redux/slices/user-work/userWorkHistorySlice';
import { fetchUserEducation } from '../redux/slices/user-education/userEducationSlice';
import { fetchOrgLevelGroup } from '../redux/slices/org-level-group/orgLevelGroupSlice';
import PasswordAndSecurity from '../pages/new-designs/tabs/profile/PasswordAndSecurity';
import AssessmentQuestions from '../pages/new-designs/tabs/profile/AssessmentQuestions';
import { acsTokenActions, fetchAcsToken } from '../redux/slices/acs-token/acsTokenSlice';
import OnboardingProcessNew from '../pages/new-designs/tabs/profile/OnboardingProcessNew';
import OrganizationSettings from '../pages/new-designs/tabs/profile/OrganizationSettings';
import { fetchCommunityMember } from '../redux/slices/communityMembers/communityMembersSlice';
import ProgramUsers from '../pages/new-designs/tabs/Program/components/program tabs/ProgramUsers';
import { ChatBotHistoryComponent } from '../pages/new-designs/tabs/livechat/ChatBotHistoryComponent';
import ProgramDetails from '../pages/new-designs/tabs/Program/components/program tabs/ProgramDetails';
import ProgramOverview from '../pages/new-designs/tabs/Program/components/program tabs/ProgramOverview';
import ProgramSettings from '../pages/new-designs/tabs/Program/components/program tabs/ProgramSettings';
import ProgramDesigner from '../pages/new-designs/tabs/Program/components/program tabs/ProgramDesigner';
import ContentSettings from '../pages/new-designs/tabs/Program/components/program tabs/ContentSettings';
import ProgramInsights from '../pages/new-designs/tabs/Program/components/program tabs/ProgramInsights';
import InsightsPage from '../pages/new-designs/tabs/Program/components/program tabs/InsightsPage';
import * as amplitude from '@amplitude/analytics-browser';
import { eventTracking, events, attributeKeys } from '../utils/amplitude';
import { initAmplitude } from '../utils/amplitudeInit';
// import { blockedMails } from '../utils/amplitude';
import { NewProgramCreation } from '../components/admin3/newProgram';
import { ProgramCreationProvider } from '../components/admin3/newProgram/ProgramCreationContext';
import { ProgramCreationFlow } from '../components/admin3/newProgram/ProgramCreationFlow';
import { Step1_SelectType } from '../components/admin3/newProgram/Step1_SelectType';
import { Step2_ModeSelection } from '../components/admin3/newProgram/masterclass/Step2_ModeSelection';
import { StepAI_Purpose } from '../components/admin3/newProgram/masterclass/ai/StepAI_Purpose';
import { StepAI_Objectives } from '../components/admin3/newProgram/masterclass/ai/StepAI_Objectives';
import StepAI_Documents from '../components/admin3/newProgram/masterclass/ai/StepAI_Documents';
import { StepAI_Review } from '../components/admin3/newProgram/masterclass/ai/StepAI_Review';
import { StepAI_Members } from '../components/admin3/newProgram/masterclass/ai/StepAI_Members';
import { ManualProgramBuilder } from '../components/admin3/newProgram/masterclass/manual/ManualProgramBuilder';
import { Step2_ModeSelection as GuidedStep2_ModeSelection } from '../components/admin3/newProgram/guidedMasterclass/Step2_ModeSelection';
import GuidedStepAI_Purpose from '../components/admin3/newProgram/guidedMasterclass/ai/StepAI_Purpose';
import GuidedStepAI_Objectives from '../components/admin3/newProgram/guidedMasterclass/ai/StepAI_Objectives';
import GuidedStepAI_Documents from '../components/admin3/newProgram/guidedMasterclass/ai/StepAI_Documents';
import GuidedStepAI_Review from '../components/admin3/newProgram/guidedMasterclass/ai/StepAI_Review';
import GuidedStepAI_Members from '../components/admin3/newProgram/guidedMasterclass/ai/StepAI_Members';
import GuidedManualProgramBuilder from '../components/admin3/newProgram/guidedMasterclass/manual/GuidedManualProgramBuilder';
import MemberPortfolioSwitch from '../pages/new-designs/tabs/home/member-learning-lifecycle/MemberPortfolioSwitch';
import CourseDeepLink from '../pages/new-designs/tabs/home/CourseDeepLink';
import { MentorshipProgramName } from '../components/admin3/newProgram/mentorship/MentorshipProgramName';
import { MentorshipObjectives } from '../components/admin3/newProgram/mentorship/MentorshipObjectives';
import { MentorshipMembers } from '../components/admin3/newProgram/mentorship/MentorshipMembers';
import OrgMembersPage from '../pages/new-designs/tabs/OrgMembers/OrgMembersPage';
import IntegrationsPage from '../pages/new-designs/tabs/integrations/IntegrationsPage';

/**
 * AdminRoutes for Admins (Platform or Organization)
 * @returns jsx
 */
const NewPlatformAdminRoutes = (
  <Route path="/app" element={<ProtectedRoute permissions={[ROLES.platAdmin]} />}>
    <Route element={<SecuredRoutes />}>
      <Route path="" element={<Layout />}>
        <Route path="organizations" element={<OrganizationsElement />} />
        <Route path="elliott-conversations" element={<ElliottConversationsElement />} />
        <Route path="course-creation-time" element={<CourseCreationTimeElement />} />
      </Route>
    </Route>
  </Route>
);

/**
 * UserRoutes for normal member (Other than admin)
 * @returns jsx
 *
 * NewDesign Routes
 * @function @name AppRoutes
 * @description It contains all the routes used in the application
 * @returns {React.ReactElement}
 */

const AppRoutes: React.FC = () => {
  const { isLoading, isAuthenticated, getAccessTokenSilently, getIdTokenClaims, user } = useAuth0();
  const dispatch = useAppDispatch();
  const urlLocation = useLocation();
  const { isAdmin, isMentor } = useUserRole();
  const { isMasterclass, isHybrid } = useModule();
  const urlParams = new URLSearchParams(urlLocation.search);
  const isPromptLogin = urlParams.get('prompt') || '';
  const promptOrg = urlParams.get('org') || '';
  const { admins, mentee, mentors, ftue } = useProfileDetails();
  const { orgName } = getUserDetails();
  const queryClient = useQueryClient();
  // The Capability release is per org while it is piloted (utils/capabilityRelease.ts).
  // Mirrored here, before any page renders, for the tier names and other plain code.
  // The learner's own pathway shows what members get, so an admin who switches to it
  // in admin mode sees the app without the release, as their members do.
  const capabilityRelease = useCapabilityRelease();
  const releaseForMembers = useCapabilityReleaseForMembers();
  setCapabilityRelease(urlLocation.pathname.startsWith('/app/home/user') ? releaseForMembers : capabilityRelease);
  // const groupSettings = useGroupSettings();
  const selectedChatUser = useAppSelector((state) => state.selectedChatUser.data);
  const acsToken = useAppSelector((state) => state.acsToken?.data?.token);
  const acsTokenLoading = useAppSelector((state) => state.acsToken.loading);
  const role = localStorage.getItem('role') || '';
  const [roleLoading, setRoleLoading] = useState(false);
  const userProfile = useAppSelector((state) => state.userProfile);
  const orgDetails = useAppSelector((state) => state.orgDetails);
  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const groupId: any = selectedGroupId.id || '';
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  // const loginUser = useAppSelector((state) => state.userProfile.data?.personal_details);
  // const allArticleData = useAppSelector((state) => state.allArticleUser?.data?.ArticleList) ?? [];

  const connection = userProfile.data?.connection;

  // Dynamic redirection based on group based roles
  const getHomeTab = () => {
    const role = localStorage.getItem('role') || '';
    if (role === ROLES.platAdmin) {
      return null;
    }
    // if (
    //   (role === ROLES.member || role === ROLES.grpAdmin) &&
    //   admins.length > 0 &&
    //   mentee.length === 0 &&
    //   mentors.length === 0
    // ) {
  //   return null;
  // }

  return (
    <Route path="home" element={<HomeTab />}>
      <Route element={<SecuredRoutes />}>
        {/* HomeTab is only an <Outlet />, so /app/home with no child segment
            rendered an empty page under the header. Several places navigate
            there (the Sidebar and the video footer when the member holds no
            mentee/mentor entries, and the role routes below when their list is
            empty), which showed as a blank screen after "Next Lesson". The
            member dashboard is the right landing for every role. */}
        <Route index element={<Navigate to="user" replace />} />
        <Route path="user" element={<MemberPortfolioSwitch />} />
        {/* One course, e.g. from an Elliott recommendation — opens its drawer on the dashboard. */}
        <Route path="course/:grpId" element={<CourseDeepLink />} />
        {orgName !== AGILE_ORG && (
          <Route
            path="mentee"
            element={
              mentee?.length > 0 ? (
                  selectedGroup.active ? (
                    <Mentee />
                  ) : (
                    <DeactivatedUser />
                  )
                ) : (
                  <Navigate to="/app/home/user" replace />
                )
              }
            />
          )}
          <Route
            path="mentor"
            element={
              mentors?.length > 0 ? (
                selectedGroup.active ? (
                  <Mentor />
                ) : (
                  <DeactivatedUser />
                )
              ) : (
                <Navigate to="/app/home/user" replace />
              )
            }
          />
        </Route>
      </Route>
    );
  };

  const tokens = localStorage.getItem('tokens');
  const { exp, userId } = getUserDetails();
  const currentTime = moment();
  const expiryTime = moment.unix(exp);

  /** Get channels */
  const getChannels = async () => {
    // if (acsToken && role !== ROLES.platAdmin) {
    const tokens = localStorage.getItem('tokens');
    const isLoggedIn = localStorage.getItem('isAuthenticated');
    if (role && tokens && !currentTime.isAfter(expiryTime) && acsToken && isAuthenticated && isLoggedIn) {
      const ctx = getProgramContext();
      dispatch(
        fetchCommunityMembers({
          orgId: ctx.orgId,
          location: ctx.location
        })
      );
    }
  };

  const groupRoleList = useAppSelector((state) => state.groupRoleList);
  const { doLogout } = useLogout();

  /** Progress chart value  */
  const fetchProgressChartValue = async (orgId: string, grpId: string, loc: string) => {
    // setProgramRoiLoading(true);
    try {
      const data = { orgId, grpId, loc };
      const response = await API.getGroupsOverview(data); // Replace with your API call
    } catch (e) {
      //
    } finally {
      // setProgramRoiLoading(false);
    }
  };

  /**
   * Preload queries Section
   */
  // useEffect(() => {
  //   if (acsToken && selectedChatUser.communicationId === '') {
  //     getChannels();

  //     dispatch(
  //       fetchCommunityMembers({
  //         orgId: getUserDetails().orgId,
  //         location: getUserDetails().location
  //       })
  //     );

  //     dispatch(
  //       fetchAllArticlesUser({ orgId: getUserDetails().orgId, type: [], tags: [], industries: [], helpSettings: false })
  //     );

  //     if (allArticleData[0]?.articleId) {
  //       fetchArticle();
  //     }
  //   }
  // }, [acsToken]);

  // const userCallsDataRefetch = () => {
  //   const userCallsData = useCallDetails(1, 10);
  //   userCallsData.refetch();
  // };

  const { data, error, refetch, allTotelCount } = useCallDetails(1, 10);
  const isLoggedIn = localStorage.getItem('isAuthenticated');

  useEffect(() => {
    const delay = 5000; // 5 seconds in milliseconds
    const timerId = setTimeout(() => {
      if (acsToken && selectedChatUser.communicationId === '') {
        getChannels();
      }

      if (isLoggedIn) {
        refetch();
        const ctx = getProgramContext();
        dispatch(fetchCommunityMember({ orgId: ctx.orgId, location: ctx.location }));

        dispatch(
          fetchCommunityMembers({
            orgId: ctx.orgId,
            location: ctx.location
          })
        );

        if (window.performance) {
          const navigationEntries = performance.getEntriesByType('navigation');
          if (navigationEntries.length > 0) {
            const navigationEntry = navigationEntries[0] as PerformanceNavigationTiming;
            if (navigationEntry.type === 'reload') {
              // Handle the page reload scenario
            } else {
              if (role !== ROLES.platAdmin) {
                dispatch(
                  fetchAllArticlesUser({
                    orgId: ctx.orgId,
                    type: [],
                    tags: [],
                    industries: [],
                    helpSettings: false,
                    useCache: true
                  })
                );
                dispatch(
                  fetchAllArticlesUser({
                    orgId: ctx.orgId,
                    type: [],
                    tags: [],
                    industries: [],
                    helpSettings: true,
                    useCache: true
                  })
                );
              }

              // Handle normal app navigation scenario
            }
          }
        }

        // getChatThread();
        if (isAdmin) {
          fetchProgressChartValue(ctx.orgId, groupId, ctx.location);
        }
      }
    }, delay);
    // Cleanup function to clear the timeout in case the component is unmounted before the timeout  completes
    return () => clearTimeout(timerId);
  }, [isLoggedIn]);

  // When the threads fetch last renewed a refused ACS token (see its queryFn).
  const lastAcsRenewalRef = useRef(0);
  const {
    isError,
    error: chatThreadError,
    data: threadData,
    isSuccess
  } = useQuery({
    queryKey: ['getAcsChatThreads'],
    queryFn: async () => {
      const token = window.localStorage.getItem('acsToken');
      const { orgId } = getProgramContext();
      const tag = selectedChatUser.communicationId ? '#admin' : '#self';
      const communicationId = selectedChatUser.communicationId
        ? selectedChatUser.communicationId
        : getUserDetails().communicationUserId;

      const [personalThreadsArr, groupThreads] = await Promise.all([
        API.getPersonalThreads({ orgId, acsToken: acsToken || token || '', tag, communicationId }),
        API.getGroupThreads({ orgId, acsToken: acsToken || token || '', tag, communicationId })
      ]);

      // An expired ACS token answers 401, which the response interceptor in App.tsx swallows: the calls
      // resolve to undefined and the query's isError never fires. Renew the token here then (at most
      // once a minute, so a token the server keeps refusing can't loop); its change fetches the threads
      // again (the acsToken effect below). Without it Chat had none of the learner's conversations, and
      // a message notification couldn't open the one it was about.
      if ((!personalThreadsArr || !groupThreads) && Date.now() - lastAcsRenewalRef.current > 60_000) {
        lastAcsRenewalRef.current = Date.now();
        dispatch(fetchAcsToken({ communicationId: getUserDetails().communicationUserId, tag }));
      }
      return { personalThreadsArr: personalThreadsArr?.data, groupThreads: groupThreads?.data };
    },
    retry: 0,

    enabled: false
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch(acsChannelActions.atnSaveChannels(threadData));
    }
  }, [isSuccess, threadData]);

  useEffect(() => {
    if (isError) {
      if (!acsTokenLoading) {
        if (getUserDetails().communicationUserId) {
          dispatch(
            fetchAcsToken({
              communicationId: getUserDetails().communicationUserId,
              tag: selectedChatUser.communicationId ? '#admin' : '#self'
            })
          );
        }
        if (_.get(chatThreadError, 'response.status') === 401) {
          dispatch(
            fetchAcsToken({
              communicationId: getUserDetails().communicationUserId,
              tag: selectedChatUser.communicationId ? '#admin' : '#self'
            })
          );
        }
      }
    }
  }, [isError]);

  /**  End of preload queries */
  // A new token always fetches: the list cached under the old one may be the empty answer of a token
  // the server refused, and fetchQuery would hand that back for the client's 30s staleTime, so a
  // renewed token never brought the conversations in.
  useEffect(() => {
    if (!checkSession() && acsToken) {
      // fetchQuery rejects when the threads fail, and nothing here awaits it: a
      // hiring candidate, refused these endpoints by design (C-9), surfaced
      // that as an uncaught AxiosError on their first page. The query's own
      // isError already carries the failure; the rejection has nothing to add.
      queryClient.fetchQuery({ queryKey: ['getAcsChatThreads'], staleTime: 0 }).catch(() => {});
    }
  }, [acsToken]);

  // **** the tab is not open or the opened tab is not doing anything
  const lastInteractionTimeRef = useRef<number>(Date.now());
  const intervalRef = useRef<number | null>(null);
  const timeLimit = 20 * 60 * 1000;
  const intervalDuration = 5 * 10 * 1000;

  const handleInteraction = () => {
    lastInteractionTimeRef.current = Date.now();
  };

  useEffect(() => {
    // document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('keydown', handleInteraction);
    intervalRef.current = window.setInterval(() => {
      const inactiveDuration = Date.now() - lastInteractionTimeRef.current;
      if (inactiveDuration >= timeLimit && !location.pathname.includes('video-call')) {
        window.location.reload();
      }
    }, intervalDuration);

    return () => {
      // document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);

      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  const chatClientRef = useRef<ChatClient | null>(null);

  chatClientRef.current = acsToken
    ? new ChatClient(
        process.env.REACT_APP_COMMUNICATION_SERVICES_ENDPOINT || '',
        new AzureCommunicationTokenCredential(acsToken)
      )
    : null;

  const handleMessageEdited = async (e: any) => {
    const senderCommunicationId = _.get(e, 'sender.communicationUserId');
    if (store.getState().userProfile.data?.personal_details?.communicationId !== senderCommunicationId) {
      const queryData: any = queryClient.getQueryData(['recieveMessages', e.threadId]);
      if (queryData) {
        const message = queryData.pages[0].value.find((each: any) => each.id === e.id);
        if (message) {
          queryClient.setQueryData(['recieveMessages', e.threadId], (prevData: any) => {
            const value = prevData.pages[0].value;
            const newValues = value.map((each: any) => {
              if (each.id === e.id) {
                return {
                  ...each,
                  editedOn: new Date(e.editedOn),
                  content: { message: e.message },
                  metadata: e.metadata
                };
              }
              return each;
            });
            dispatch(acsChannelActions.updateLastMessageEdited(e));
            return { ...prevData, pages: [{ value: newValues }] };
          });
        }
      }
    }
  };

  const handleMessageReceived = async (e: any) => {
    const senderCommunicationId = _.get(e, 'sender.communicationUserId');
    const result = senderCommunicationId === store.getState().userProfile.data?.personal_details?.communicationId;
    if (!result) {
      const queryData = queryClient.getQueryData(['recieveMessages', e.threadId]);
      if (queryData) {
        queryClient.setQueryData(['recieveMessages', e.threadId], (prevData: any) => {
          const value = prevData.pages[0].value;
          const sequenceId = String(Number(value[0].sequenceId) + 1);
          const { id, type, version, metadata, createdOn, senderDisplayName } = e;
          const senderCommunicationIdentifier = {
            rawId: e.sender.communicationUserId,
            communicationUser: e.sender.communicationUserId
          };
          const newValue = [
            {
              id,
              type: type.toLowerCase(),
              sequenceId,
              version,
              metadata,
              createdOn,
              senderDisplayName,
              content: { message: e.message },
              senderCommunicationIdentifier
            },
            ...value
          ];
          // updates notification count and last message
          dispatch(acsChannelActions.updateLastPersonalMessage(e));
          return { ...prevData, pages: [{ value: newValue }] };
        });
      } else {
        // updates notification count and last message
        dispatch(acsChannelActions.updateLastPersonalMessage(e));
      }
    }
  };

  const handleThreadCreated = async (e: any) => {
    if (e.createdBy.id.communicationUserId !== store.getState().userProfile.data?.personal_details?.communicationId) {
      try {
        const ctx = getProgramContext();
        const response = await API.getCommunityGroupsUsersListNewDuplicate({
          orgId: ctx.orgId,
          location: ctx.location
        });
        if (response.status === 200) {
          const data = modifyCommunity(response);
          dispatch(communityUsersActions.updateData(data));
        }
      } catch (e) {
        toast.error(_.get(e, 'response.data.message', `Couldn't fetch community users`));
      }

      await queryClient.fetchQuery({ queryKey: ['getAcsChatThreads'] }).then((res) => {});
    }
  };

  const handleThreadPropertiesUpdate = async (e: any) => {
    queryClient.fetchQuery({ queryKey: ['recieveMessages', e.threadId] });
  };

  const handleMessageDeleted = async (e: any) => {
    if (e?.sender?.communicationUserId !== getUserDetails().communicationUserId) {
      queryClient.fetchQuery({
        queryKey: ['recieveMessages', e.threadId]
      });
    }
  };

  const handleParticipantsRemoved = async (e: any) => {
    queryClient.fetchQuery({
      queryKey: ['recieveMessages', e.threadId]
    });
  };

  const chatClient = chatClientRef.current;

  useEffect(() => {
    if (acsToken) {
      chatClient?.startRealtimeNotifications();
      chatClient?.on('chatMessageEdited', handleMessageEdited);
      chatClient?.on('chatMessageReceived', handleMessageReceived);
      chatClient?.on('chatThreadCreated', handleThreadCreated);
      chatClient?.on('participantsRemoved', handleParticipantsRemoved);
      chatClient?.on('chatThreadPropertiesUpdated', handleThreadPropertiesUpdate);
      chatClient?.on('chatMessageDeleted', handleMessageDeleted);
    }
    return () => {
      chatClient?.off('chatMessageEdited', handleMessageEdited);
      chatClient?.off('chatMessageReceived', handleMessageReceived);
      chatClient?.off('chatThreadCreated', handleThreadCreated);
      chatClient?.off('participantsRemoved', handleParticipantsRemoved);
      chatClient?.off('chatThreadPropertiesUpdated', handleThreadPropertiesUpdate);
      chatClient?.off('chatMessageDeleted', handleMessageDeleted);
    };
  }, [acsToken]);

  const allowedAdmins = [ROLES.orgAdmin, ROLES.platAdmin];

  const navigate = useNavigate();
  const [settingsLoader, setSettingsLoader] = useState(false);
  const companyFlag = useAppSelector((state) => state.company.flag);

  const getToken = async () => {
    setRoleLoading(true);
    const token = await getAccessTokenSilently();
    const claims = await getIdTokenClaims();
    const id_token = claims?.__raw;
    localStorage.setItem('tokens', JSON.stringify({ access_token: token, id_token }));
    const data: any = jwt_decode(token);
    const idData = jwt_decode(id_token || '');
    const getRole = data['https://dosen.io/role'];
    const orgDetails = data['https://dosen.io/organization'];
    const fullName = _.get(idData, 'user_metadata.name', '');
    // const lastName = _.get(idData, 'user_metadata.lastName', '');
    // const fullName = `${firstName} ${lastName}`.trim();
    // console.log("idData : ", idData);
    localStorage.setItem('username', fullName);
    window.s247r('userId', fullName);

    const roleMappings: Record<number, string> = {
      1: ROLES.member,
      2: ROLES.orgAdmin,
      3: ROLES.platAdmin,
      4: ROLES.grpAdmin
    };

    let role = roleMappings[getRole?.role];
    role !== ROLES.platAdmin ? localStorage.setItem('orgLocation', _.get(orgDetails, 'metadata.location', '')) : null;
    await localStorage.setItem('role', role);
    setRoleLoading(false);

    try {
      setSettingsLoader(true);
      const response = await API.userBasicSettings({ userId: _.get(idData, 'sub', '') });
      if (response.status === 200) {
        const { orgId, location } = getUserDetails();
        if (role && rolesArray.includes(role)) {
          localStorage.setItem('isAuthenticated', JSON.stringify(true));
          /**
           * Materialize any invited_users rows for this email into group
           * membership. It was awaited here, which put a call averaging 15.3s
           * in real-user data ahead of everything else in startup, for every
           * non-platform-admin login — and nothing on this path reads its
           * result. It now runs alongside the rest of startup.
           *
           * On the rare login where it does materialize something, `synced`
           * comes back non-zero and the profile and cached lists are refreshed,
           * so the new programs appear without the user reloading the page.
           */
          if (role !== ROLES.platAdmin) {
            API.syncPendingInvites()
              .then((res: any) => {
                if (!res?.data?.synced) return;
                dispatch(fetchUserProfile());
                // The program list was likely read (and cached for 5 minutes) before the sync landed.
                dispatch(fetchUserGroupList({ refresh: true }));
                queryClient.invalidateQueries();
              })
              .catch(() => {
                /* non-blocking: multi-program invite reconciliation */
              });
          }
          dispatch(fetchUserProfile()).then((res: any) => {
            setSettingsLoader(false);
            // The JWT role claim is org-scoped for platform admins who are also
            // members of an org (Auth0 org picker), so it can resolve to their
            // in-org role. The DB role is authoritative (mirrors BE handler) —
            // persist Platform Admin to localStorage so route guards and refreshes
            // recognize them correctly.
            if (_.get(res, 'payload.data.role') === 3) {
              localStorage.setItem('role', ROLES.platAdmin);
            }
            const memberRole = _.capitalize(_.get(res, 'payload.data.lastseen.role', ''));
            const programName = _.capitalize(_.get(res, 'payload.data.lastseen.name', ''));
            const memberType = _.capitalize(_.get(res, 'payload.data.personal_details.category', ''));
            const userEmail = _.get(res, 'payload.data.personal_details.email', '');
            try {
              const rawInv = localStorage.getItem('invitation');
              if (rawInv && rawInv !== 'null' && rawInv !== 'undefined' && role && role !== ROLES.platAdmin) {
                if (!sessionStorage.getItem('amp_user_accepted_evt')) {
                  sessionStorage.setItem('amp_user_accepted_evt', '1');
                  eventTracking(events.userAccepted, {
                    [attributeKeys.userEmail]: userEmail,
                    [attributeKeys.organizationName]: _.get(orgDetails, 'display_name', ''),
                    [attributeKeys.organizationType]: _.get(orgDetails, 'metadata.orgType', ''),
                    [attributeKeys.programName]: programName,
                    [attributeKeys.userRole]: memberRole
                  });
                }
              }
            } catch {
              /* noop */
            }
            if (role && role !== ROLES.platAdmin) {
              // Amplitude + Session Replay are already initialized at app boot
              // (see initAmplitude in index.tsx). Re-initializing here is a no-op
              // in SDK v2 and double-adds the replay plugin, so we only identify.
              initAmplitude();

              const identify = new amplitude.Identify();
              identify.set('Organization', _.get(orgDetails, 'display_name', ''));
              identify.set('Organization type', _.get(orgDetails, 'metadata.orgType', ''));
              identify.set('Member role', memberRole);
              identify.set('Member type', memberType);
              identify.set('Program', programName);
              amplitude.identify(identify);
            }

            const getMatches = async () => {
              try {
                const response = await API.bulkMatches({ orgId, location });
              } catch (e) {}
            };
            // if (role !== ROLES.platAdmin) {
            //   getMatches();
            // }

            if (allowedAdmins.includes(role) || (res?.payload && res?.payload?.data?.admins.length > 0)) {
              dispatch(fetchChatUsers(''));
            }
            if (ftue && userId && role !== ROLES.platAdmin) {
              dispatch(fetchUserEducation(userId));
              dispatch(fetchUserWorkHistory(userId));
            }
            if (res?.payload && res?.payload?.data?.mentors.length > 0) {
              const ftue = res?.payload?.data?.personal_details?.ftue;
              if (ftue || (groupId && isMentor)) {
                dispatch(fetchMentorTasks({ groupId }));
              }
            }
          });

          // dispatch(
          //   fetchCommunityMembers({
          //     orgId: getUserDetails().orgId,
          //     location: getUserDetails().location
          //   })
          // );
          if (selectedChatUser.communicationId === '') {
            dispatch(
              fetchAcsToken({
                communicationId: getUserDetails().communicationUserId,
                tag: selectedChatUser.communicationId ? '#admin' : '#self'
              })
            );
          }

          if (role === ROLES.orgAdmin) {
            dispatch(fetchOrgLevelGroup(_.get(idData, 'user_metadata.coId', '')));
          }
          if (role !== ROLES.platAdmin) {
            dispatch(fetchUserGroupList());
            dispatch(fetchOrgDetails(_.get(orgDetails, 'id', '')));
          }
          dispatch(fetchCallsCount(location));
        }
      }
    } catch (e) {
      setSettingsLoader(false);
      // A Hiring candidate is refused this call on purpose (DA-5042 C-9): it is
      // the member first-time setup's own endpoint and they are not a member.
      // The refusal names itself, so for them it is a route and not an error —
      // without this they land on the error page and can never reach their
      // assessment.
      if (isCandidateRefusal(e)) {
        rememberCandidateRefusal();
        navigate('/hiring', { replace: true });
        return;
      }
      navigate('/errorpage', {
        state: { message: _.get(e, 'response.data.message', 'User basic configurations failed') }
      });
    }
  };

  const updateCompanyList = async () => {
    const company = localStorage.getItem('company');
    if (company) {
      const { name: companyName, location: companyLocation } = JSON.parse(company);
      if (!companyName || companyName.trim() === '') return;
      try {
        const response = await API.addCompany({ companyName, companyLocation });
        if (response.status === 200) {
          dispatch(companyActions.update(false));
          localStorage.removeItem('company');
        }
      } catch (e) {
        toast.error(_.get(e, 'response.data.message', `Couldn't add company`));
      }
    }
  };

  useEffect(() => {
    if (companyFlag) {
      updateCompanyList();
    }
  }, [companyFlag]);

  // useEffect(() => { dispatch(fetchUserGroupList()) }, [groupId])

  const location = useLocation();
  const currentLocation = useLocation();

  useEffect(() => {
    if (
      !checkSession() &&
      !orgDetails.loading &&
      (role === ROLES.orgAdmin || role === ROLES.platAdmin || role === ROLES.grpAdmin) &&
      currentLocation.pathname.includes('/app/programdetails')
    ) {
      const ctx = getProgramContext();
      dispatch(fetchOrgDetails(ctx.orgId));
    }
  }, []);

  useEffect(() => {
    const clearAll = () => {
      if (selectedChatUser.communicationId !== '') {
        localStorage.removeItem('user');
        localStorage.removeItem('chatAccessed');
        localStorage.removeItem('acsToken');
        dispatch(acsTokenActions.updateAcsToken(0));
        dispatch(
          fetchAcsToken({
            communicationId: getUserDetails().communicationUserId,
            tag: selectedChatUser.communicationId ? '#admin' : '#self'
          })
        ).then(() => {
          queryClient.fetchQuery({ queryKey: ['getAcsChatThreads'] });
        });
        dispatch(communityUsersActions.update(0));
        // dispatch(
        //   fetchCommunityMembers({
        //     orgId: getUserDetails().orgId,
        //     location: getUserDetails().location
        //   })
        // );
        dispatch(
          acsChannelActions.atnSaveChannels2({
            groupThreads: [],
            personalThreadsArr: []
          })
        );
        dispatch(
          selectedChatUserActions.update({
            communicationId: '',
            userId: '',
            name: ''
          })
        );
      }
    };

    if (!location.pathname.includes('chat')) {
      clearAll();
    }
  }, [location]);

  useEffect(() => {
    dispatch(popupCountActions.reset(0));

    const keysToRemove = [
      'education',
      'work',
      'profileData',
      'hobbies',
      'social',
      'disability',
      'digitalIntro',
      'edFlag',
      'workFlag'
    ];
    keysToRemove.map((key) => localStorage.removeItem(key));
    if (isAuthenticated) {
      getToken();
    }
  }, [isAuthenticated]);

  /** Full page loader here */
  // const shouldShowGroupLoader = role !== ROLES.platAdmin && groupRoleList.loading; // Old condition 1
  // const isAnyLoading = isLoading || roleLoading || userProfile.loading || settingsLoader; // Old condition 2

  // No fullscreen blocker. The shell (header, sidebar, breadcrumbs) renders
  // immediately; each child component owns its loading state with local
  // skeletons. Only the Auth0 cold-start (no token at all) returns null —
  // we can't render auth-protected routes without it.
  // On the sign-in landing route ("/") show the app loader instead of a blank
  // page, so a fresh signup sees one continuous loader from Auth0 to onboarding.
  if (isLoading && !userProfile.data) {
    return location.pathname === '/' ? <AppLoader /> : null;
  }

  // Hold while an authenticated session's profile is still being fetched for
  // the FIRST time (no persisted data to fall back on). The /app/* routes
  // below are conditionally mounted on `userProfile.data`; rendering <Routes>
  // before it arrives mounts only the public routes, so the current /app/*
  // URL (kept by HashRouter across a refresh) falls through to the catch-all,
  // gets replaced with /login, and the user is bounced to their default
  // dashboard instead of the page they were on. `userProfile.loading` is only
  // true while a fetch is in flight, so this never blanks the app during an
  // in-session refetch, and a failed fetch (loading:false) falls through to
  // the normal error handling below.
  if (isAuthenticated && userProfile.loading && !userProfile.data) {
    return location.pathname === '/' ? <AppLoader /> : null;
  }

  /** New design routes */
  const NewDesignRoutes = (
    <Route
      path="/app"
      element={<ProtectedRoute permissions={[ROLES.orgAdmin, ROLES.platAdmin, ROLES.member, ROLES.grpAdmin]} />}
    >
      <Route element={<SecuredRoutes />}>
        <Route path="onboardingprocess" element={<OnboardingProcessNew />} />
        {/* Connection Problem */}
        <Route path="connectionproblem" element={<ConnectionProblem />} />
        {/* Elliott-led lesson redesign — full-bleed (outside Layout chrome) so it owns its own
            header, per Figma. Classic MenteeTask stays inside Layout at /mentee-classic.
            Gated per-org while the content backfill rolls out (utils/elliottFeatureFlag). */}
        <Route path="home/mentee/level/:levelId/task/:taskId" element={<ElliottLessonGate />} />
        {/* Taking an Assessment: the same full-bleed page as a lesson. */}
        <Route path="assessment/:assessmentId" element={<AssessmentPlayerPage />} />
        {/* The stubbed walkthrough that used to live here is gone. Anything still
            pointing at it lands on the learner's own pathway rather than on the
            login screen via the top-level catch-all. */}
        <Route path="home/elliott-lesson" element={<Navigate to="/app/home/user" replace />} />
        <Route path="" element={<Layout />}>
          {getHomeTab()}
          <Route path="redirect" element={<RoleRedirect />} />
          {/* Admin pages: Org Members, like Organization Settings, is for admins. It was mounted for
              every role and a learner opened it (QA ORG-90). */}
          <Route path="" element={<ProfileOrgSettingsGuard />}>
            <Route path="org/:orgId/members" element={<OrgMembersPage />} />
            <Route path="orgsettings" element={<OrganizationSettings />} />
            <Route path="integrations" element={<IntegrationsPage />} />
          </Route>
          <Route path="profile" element={<ProfileLayout />}>
            <Route path="" element={<Profile />} />
            <Route path="assessment" element={selectedGroup.active ? <AssessmentQuestions /> : <DeactivatedUser />} />
            <Route path="" element={<ProfileOrgSettingsGuard />}>
              <Route path="orgsettings" element={<OrganizationSettings />} />
              <Route path="settings" element={<ProfileSettings />} />
            </Route>
            {connection === 'Username-Password-Authentication' && (
              <Route path="security" element={<PasswordAndSecurity />} />
            )}
          </Route>
          {/* 
          <Route path="askDosen/" element={<AskDosen />}>
            <Route path="" element={<AiChatMain />} />
            <Route path="promtChat" element={<PromptChat />} />
          </Route>
          <Route path="liveChat" element={<LiveChat />} />

          <Route path="aithanks" element={<AIChatThanksPage />} /> */}

          {/* <Route path="aichatbot/" element={<ChatBot />}>
            <Route path="" element={<PromptCards />} />
            <Route path="chatBothistory" element={<ChatBotHistoryComponent />} />
            <Route path="newChatBot" element={<NewChatBot />} />
          </Route> */}

          {selectedGroup.active || role === ROLES.platAdmin ? (
            <>
              <Route path="calendar" element={<Calendar />} />
              {/* Knowledge and the admin Resources page are admin surfaces (QA ORG-91, ORG-92). */}
              <Route path="" element={<ProfileOrgSettingsGuard />}>
                <Route path="knowledge" element={<Knowledge />} />
                <Route path="resources" element={<ContentSettings />} />
              </Route>
              {/* Org admins and team managers only (the backend's rule), not every program admin. */}
              <Route path="" element={<CapabilitySetupGuard />}>
                <Route path="capability-setup" element={<CapabilitySetupPage />} />
                <Route path="operations" element={<OperationsPage />} />
              </Route>
              {/* Hiring (DA-5042 H-1): its own top-level section, for the same people as
                  Capability Setup and only for an org with the Hiring flag on. */}
              <Route path="" element={<HiringGuard />}>
                <Route path="hiring" element={<HiringPage />} />
                <Route path="hiring/rounds/:roundId" element={<HiringRoundPage />} />
              </Route>
              <Route path="community" element={<CommunityTab />} />
              <Route path="library" element={<LibraryTab />} />
              <Route path="help" element={<HelpTab />} />
              {/* <Route path="livechat" element={<LiveChat />} /> */}
              {/* <Route path="aithanks" element={<AIChatThanksPage />} /> */}
              {/* <Route path="aichatbot" element={<ChatBot />} /> */}
              <Route path="libraryindustries" element={<LibraryIndustries />} />
              <Route path="communitymember" element={<CommunityMember />} />
              <Route path="charlie" element={<Charlie />} />
              <Route path="charlie/:convid" element={<Charlie />} />
              <Route path="chat" element={<ChatTab />} />
              <Route path="temple" element={<TemplePage />} />
              {/* Classic lesson page — kept inside Layout chrome until cutover is decided. */}
              <Route path="/app/home/mentee-classic/level/:levelId/task/:taskId" element={<MenteeTask />} />
            </>
          ) : (
            <Route path="*" element={<Navigate to="/" />} />
          )}
          <Route path="deactivated" element={<DeactivatedUser />} />
        </Route>
      </Route>
    </Route>
  );

  /** Program routes */
  const ProgramRoutes = (
    <Route
      path="/app"
      element={<ProtectedRoute permissions={[ROLES.orgAdmin, ROLES.platAdmin, ROLES.grpAdmin, ROLES.member]} />}
    >
      <Route element={<SecuredRoutes />}>
        <Route path="" element={<Layout />}>
          <Route path="programdetails/:orgId/:coId/:id/aimd-v2" element={<AIMDv2Page />} />
          <Route path="programdetails/:orgId/:coId/:id" element={<ProgramDetails />}>
            {selectedGroup.active || role === ROLES.platAdmin ? (
              <>
                <Route path="overview" element={<ProgramOverview />} />
                {/* <Route path="new-program" element={<NewProgramCreation />} /> */}
                <Route
                  path="new-program/*"
                  element={
                    <ProgramCreationProvider>
                      <ProgramCreationFlow />
                    </ProgramCreationProvider>
                  }
                >
                  <Route index element={<Navigate to="start" replace />} />
                  <Route path="start" element={<Step1_SelectType />} />
                  <Route path="masterclass">
                    <Route path="step-2" element={<Step2_ModeSelection />} />
                    <Route path="manual">
                      <Route index element={<ManualProgramBuilder model="masterclass" hydrateFromUrl />} />
                      <Route path="members" element={<StepAI_Members />} />
                      </Route>
                      <Route path="ai">
                      <Route path="purpose" element={<StepAI_Purpose />} />
                      <Route path="objectives" element={<StepAI_Objectives />} />
                      <Route path="documents" element={<StepAI_Documents />} />
                      <Route path="review" element={<StepAI_Review />} />
                      <Route path="members" element={<StepAI_Members />} />
                    </Route>
                  </Route>
                  <Route path="guided-masterclass">
                    <Route path="step-2" element={<GuidedStep2_ModeSelection />} />
                    <Route path="manual">
                      <Route index element={<GuidedManualProgramBuilder />} />
                      <Route path="members" element={<GuidedStepAI_Members />} />
                    </Route>
                    <Route path="ai">
                      <Route path="purpose" element={<GuidedStepAI_Purpose />} />
                      <Route path="objectives" element={<GuidedStepAI_Objectives />} />
                      <Route path="documents" element={<GuidedStepAI_Documents />} />
                      <Route path="review" element={<GuidedStepAI_Review />} />
                      <Route path="members" element={<GuidedStepAI_Members />} />
                    </Route>
                  </Route>
                  <Route path="mentorship">
                    <Route path="program-name" element={<MentorshipProgramName />} />
                    <Route path="learning-objectives" element={<MentorshipObjectives />} />
                    <Route path="members" element={<MentorshipMembers />} />
                  </Route>
                  {/* ... resto dos steps */}
                </Route>
                <Route path="users" element={<ProgramUsers />} />
                <Route
                  path="matches"
                  element={
                    // Guided Course (masterclass&mentorship = isHybrid) HAS
                    // matches — only pure Course (masterclass) has no matches.
                    isMasterclass && !isHybrid ? (
                      <Box
                        p={2}
                        borderRadius={1}
                        display={'flex'}
                        bgcolor={'#FFFFFF'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        height={'calc(100% - 99px)'}
                        border={'1px solid #EFF0F4'}
                      >
                        <Typography variant="h4" color="primary">
                          This Matches Page no needed for the Course model
                        </Typography>
                      </Box>
                    ) : (
                      <ProgramMatches />
                    )
                  }
                />
                <Route path="settings" element={<ProgramSettings />} />
                {/* orgLevelAdmin: Program Designer surface (Course / Guided Course land here) */}
                <Route path="designer" element={<ProgramDesigner />} />
              </>
            ) : (
              <Route path="*" element={<DeactivatedUser />} />
            )}
          </Route>
          <Route path="programdetails/:orgId/:coId/admin/insights" element={<ProgramInsights />} />
          <Route path="programdetails/:orgId/:coId/admin/insights-v3" element={<InsightsPage />} />
        </Route>
      </Route>
    </Route>
  );

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Redirect />} />
        <Route element={<SecuredRoutes />}>
          <Route path="/redirect" element={<RoleRedirect />} />
        </Route>
        <Route path="/download-app" element={<MobileAppLinkPage />} />
        {/* A Hiring candidate's whole world (DA-5042 section 5). Outside Layout
            on purpose: they have no nav and nothing else they can reach, which
            the backend enforces with an endpoint allow-list (PRD 6.1, C-9). */}
        <Route element={<SecuredRoutes />}>
          <Route path="/hiring" element={<CandidateApp />} />
        </Route>

        {userProfile.data ? (
          <>
            {NewPlatformAdminRoutes}
            {NewDesignRoutes}
            {(admins?.length > 0 || role === ROLES.orgAdmin || role === ROLES.platAdmin || role === ROLES.grpAdmin) && ProgramRoutes}
            <Route path="/video-call" element={<ZoomIndexElement />} />
            <Route path="/video-call/:topic/:role/:password/:startTime/:endTime" element={<ZoomIndexElement />} />
          </>
        ) : (
          <Route path="/login" element={<LoginElement />} />
        )}

        <Route path="/unauthorized" element={<Exception http={402} />} />
        <Route path="/not-found" element={<Exception http={404} />} />
        <Route
          path="*"
          element={
            isPromptLogin ? (
              <Navigate to={`/login?${promptOrg ? `org=${promptOrg}&` : ''}prompt=${isPromptLogin}`} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route path="/login" element={<LoginElement />} />
        <Route path="/comingsoon" element={<ComingSoon />} />
        <Route path="/not-foundv2" element={<NotFoundv2 />} />
        <Route path="/logout" element={<LogoutComponent />} />
        <Route path="/error" element={<ConnectionProblem />} />
        <Route path="/errorPage" element={<Error message="" />} />
        <Route path="/maintenance" element={<PlannedMaintenance />} />
        <Route path="/theme-reference" element={<ThemeReference />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
